import { call, put, select, all, take } from 'redux-saga/effects';
import * as actions from './actions';
import { Base64 } from 'js-base64';
import axios from 'axios';
import { actions as campaignActions } from 'store/campaign';
import { actions as userMessageActions } from 'store/userMessaging';
import { types } from 'constants.js';
import { waitFor } from 'store/helpers';
import * as eventSagas from 'store/event/sagas';
import { AUTO_SAVE_TIMER_MS } from 'constants.js';
import { updatePrintFilters } from './actions';
import { getParams, postMessage, safeBase64Encode, safeBase64Decode } from 'store/helpers/helperFunctions';
const { waitForGetSupportedEventTypes, waitForGetEventMetaData } = eventSagas;

export function* waitForGetPrintsTemplates() {
  return yield waitFor(
    actions.getPrintTemplates(),
    actions.GETTING_PRINT_TEMPLATES_SUCCEEDED,
    actions.GETTING_PRINT_TEMPLATES_FAILED
  );
}

// hydration and shape helpers
const decodeTemplateContent = template => {
  template.jsonContent = safeBase64Decode(template.jsonContent);
  template.content = safeBase64Decode(template.content);
  template.header = safeBase64Decode(template.header || '', false);
  template.footer = safeBase64Decode(template.footer || '', false);
  // Sanitize rows and columns to ensure they are valid numbers within limits
  const rowsNum = Number(template.rows);
  const columnsNum = Number(template.columns);
  template.rows = (rowsNum >= 1 && rowsNum <= 4) ? rowsNum : 1;
  template.columns = (columnsNum >= 1 && columnsNum <= 3) ? columnsNum : 1;
  return template;
};

const encodeTemplateContent = template => {
  function isBase64(str) {  
    try {  
      return btoa(atob(str)) == str;  
    } catch (err) {  
      return false;  
    }  
  }
  // Sanitize rows and columns to ensure they are valid numbers within limits
  const rowsNum = Number(template.rows);
  const columnsNum = Number(template.columns);
  template.rows = (rowsNum >= 1 && rowsNum <= 4) ? rowsNum : 1;
  template.columns = (columnsNum >= 1 && columnsNum <= 3) ? columnsNum : 1;
  if(!isBase64(template.jsonContent)) {
    if (template.content) {
      template.content = safeBase64Encode(template.content, true);
    } else {
      template.content = safeBase64Encode(template.jsonContent.html, true);
    }
    template.jsonContent = safeBase64Encode(template.jsonContent);
  }
  template.header = safeBase64Encode(template.header || '', false, false);
  template.footer = safeBase64Encode(template.footer || '', false, false);
  return template;
};

const setCampaignOccurrenceType = template => {
  if (!template.hasOwnProperty('campaignOccurrenceType')) {
    if (template.schemaId === 'static_scheduled') {
      template.campaignOccurrenceType = 'SCHEDULED';
    } else if (template.schemaId === 'static_onDemand') {
      template.campaignOccurrenceType = 'ON_DEMAND';
    } else {
      template.campaignOccurrenceType = 'ON_EVENT';
    }
  }

  return template;
};

// the actual http calls
const apiRoot = `${window._env_?.REACT_APP_PRINT_API_PATH}`;

const getTemplateApiRoot = (tenantId, businessContextId) =>
  `${apiRoot}tenants/${tenantId}/context/${businessContextId}`;

const getTagsApiRoot = (tenantId, businessContextId) =>
  `${apiRoot}tenants/${tenantId}/context/${businessContextId}/tags/print-template`;

let isArchiveInProgress = false;

export const getTemplatesAsync = (tenantId, businessContextId, templateId) =>
  axios
    .get(
      `${getTemplateApiRoot(tenantId, businessContextId)}${
        templateId ? `/${templateId}` : '/all/get'
      }`
    )
    .then(response => response.data)
    .catch(err => {
      throw err;
    });

export const getPaginatedTemplatesAsync = (
  tenantId,
  businessContextId,
  urlParams,
  bodyParams
) => {
  return axios
    .post(
      `${getTemplateApiRoot(tenantId, businessContextId)}/search?index=${
        urlParams.index
      }&pageSize=${urlParams.pageSize}`,
      bodyParams
    )
    .then(response => ({
      data: response.data,
      totalRecords: parseInt(response.headers['x-ag-totalcount'])
    }))
    .catch(err => {
      throw err;
    });
};

export const getTagsAsync = (tenantId, businessContextId) =>
  axios
    .get(`${getTagsApiRoot(tenantId, businessContextId)}`)
    .then(response => response.data)
    .catch(err => {
      throw err;
    });

export const createTemplateAsync = (tenantId, businessContextId, template) =>
  axios
    .post(`${getTemplateApiRoot(tenantId, businessContextId)}`, template)
    .then(response => response.data)
    .catch(err => {
      throw err;
    });

export const updateTemplateAsync = (tenantId, businessContextId, template) =>
  axios
    .put(
      `${getTemplateApiRoot(tenantId, businessContextId)}/${template.id}`,
      template
    )
    .then(response => response.data)
    .catch(err => {
      throw err;
    });

export const deleteTemplateAsync = (tenantId, businessContextId, templateId) =>
  axios
    .delete(`${getTemplateApiRoot(tenantId, businessContextId)}/${templateId}`)
    .then(response => response.data)
    .catch(err => {
      throw err;
    });

export const getSampleAsync = (tenantId, businessContextId, payload) =>
  axios({
    url: `${getTemplateApiRoot(tenantId, businessContextId)}/test`,
    method: 'POST',
    responseType: 'blob',
    data: encodeTemplateContent(payload)
  })
  .then(response => response.data)
  .catch(err => {
    throw err;
  });
// sagas

// selectors
const currentTenantId = store => store.user.selectedTenant.tenantId;
const currentBusinessContextId = store =>
  store.user.selectedBusinessContext.businessContextId;
const currentTemplate = store =>
  store.printTemplate ? store.printTemplate.current : {};
const templateList = store =>
  store.printTemplate ? store.printTemplate.list : [];
const paginatedTemplateList = store =>
  store.printTemplate ? store.printTemplate.paginatedList : [];
const productNames = store => store.events.supportedEventTypes.productNames;
const productCategories = store =>
  store.events.supportedEventTypes.productCategories;
const tags = store => (store.printTemplate ? store.printTemplate.tagList : []);
const searchInput = store =>
  store.printTemplate.filters ? store.printTemplate.filters?.searchInput : '';
const displayActive = store =>
  store.printTemplate.filters ? store.printTemplate.filters?.displayActive : true;
const sortBy = store =>
  store.printTemplate.filters ? store.printTemplate.filters?.sortBy : 'name';
const sortOrder = store =>
  store.printTemplate.filters ? store.printTemplate.filters?.sortOrder : 'ASC';
const rowsPerPage = store =>
  store.printTemplate.filters ? store.printTemplate.filters?.rowsPerPage : 10;
const pageNo = store =>
  store.printTemplate.filters ? store.printTemplate.filters?.pageNo : 0;
const totalCount = store =>
  store.printTemplate.filters ? store.printTemplate.filters?.totalCount : 0;
const filterTypes = store =>
  store.printTemplate.filters ? store.printTemplate.filters?.filterTypes : [];
const filterTemplates = store =>
  store.printTemplate.filters ? store.printTemplate.filters?.filterTemplates : [];
const filterProductNames = store =>
  store.printTemplate.filters ? store.printTemplate.filters?.filterProductNames : [];
const filterProductCategories = store =>
  store.printTemplate.filters ? store.printTemplate.filters?.filterProductCategories : [];
const filterTags = store =>
  store.printTemplate.filters ? store.printTemplate.filters?.filterTags : [];
// helper saga to prevent culling the saga tree on rejection of a parallelized task.
function* reflect(callArgs) {
  try {
    return { value: yield call(...callArgs), status: 'resolved' };
  } catch (ex) {
    return ex;
  }
}

export function* getTemplatesIfNeeded(action) {
  const { payload = {} } = action;
  try {
    let tenantId = yield select(currentTenantId);
    let businessContextId = yield select(currentBusinessContextId);
    let currentList = yield select(templateList);
    if (
      action.type === actions.GET_PRINT_TEMPLATES &&
      (currentList.length === 0 || payload.force === true)
    ) {
      yield put(actions.gettingPrintTemplates());
      yield call(waitForGetSupportedEventTypes);
      currentList = yield call(getTemplatesAsync, tenantId, businessContextId);
      currentList = currentList.map(decodeTemplateContent);
      currentList = currentList.map(setCampaignOccurrenceType);
    }

    yield put(actions.gettingPrintTemplatesSucceeded(currentList));
  } catch (ex) {
    yield put(actions.gettingPrintTemplatesFailed(ex));
  }
}

export function* getPaginatedTemplatesIfNeeded(action) {
  const { payload = {} } = action;
  try {
    let tenantId = yield select(currentTenantId);
    let businessContextId = yield select(currentBusinessContextId);
    let currentList = yield select(paginatedTemplateList);
    yield call(waitForGetSupportedEventTypes);
    let productCategory = yield select(productCategories);
    let productName = yield select(productNames);

    if (
      action.type === actions.GET_PAGINATED_PRINT_TEMPLATES &&
      (currentList.length === 0 || payload.force === true)
    ) {
      const requestBody = {
        name: yield select(searchInput),
        sortBy: yield select(sortBy),
        sortOrder: yield select(sortOrder),
        active: yield select(displayActive),
        type: yield select(filterTypes),
        searchScopes: yield select(filterTemplates),
        productName: yield select(filterProductNames),
        productCategory: yield select(filterProductCategories),
        tags: yield select(filterTags)
      };

      let urlParams = {
        index: yield select(pageNo),
        pageSize: yield select(rowsPerPage)
      };
      let bodyParams = {
        ...getParams(requestBody, productName, productCategory)
      };
      let listCount = yield select(totalCount);
      if (
        isArchiveInProgress &&
        listCount - 1 &&
        urlParams.index >= (listCount - 1) / urlParams.pageSize
      ) {
        urlParams.index = urlParams.index - 1;
        yield put(updatePrintFilters({ pageNo: urlParams.index }));
      }
      yield put(actions.gettingPaginatedPrintTemplates());
      currentList = yield call(
        getPaginatedTemplatesAsync,
        tenantId,
        businessContextId,
        urlParams,
        bodyParams
      );
      yield put(updatePrintFilters({ totalCount: currentList.totalRecords }));
      currentList = currentList.data.map(decodeTemplateContent);
      currentList = currentList.map(setCampaignOccurrenceType);
    }
    isArchiveInProgress = false;
    yield put(actions.gettingPaginatedPrintTemplatesSucceeded(currentList));
  } catch (ex) {
    isArchiveInProgress = false;
    yield put(updatePrintFilters({ totalCount: 0 }));
    yield put(actions.gettingPaginatedPrintTemplatesFailed(ex));
  }
}

export function* getTagsIfNeeded(action) {
  try {
    let tenantId = yield select(currentTenantId);
    let businessContextId = yield select(currentBusinessContextId);
    let tagList = yield select(tags);

    if (action.type === actions.GET_PRINT_TAGS) {
      yield put(actions.gettingPrintTags());
      tagList = yield call(getTagsAsync, tenantId, businessContextId);
    }
    yield put(actions.gettingPrintTagsSucceeded(tagList));
  } catch (ex) {
    yield put(actions.gettingPrintTagsFailed(ex));
  }
}

export function* getTemplateIfNeeded(action) {
  const { payload = {} } = action;
  try {
    yield put(actions.gettingPrintTemplate());
    let tenantId = yield select(currentTenantId);
    let businessContextId = yield select(currentBusinessContextId);
    let template = yield select(currentTemplate);
    let templateId = payload.templateId;

    let yieldAlls = {
      supportedEventTypes: yield call(waitForGetSupportedEventTypes)
    };

    if (template.id !== templateId || payload.force === true) {
      template = yield call(
        getTemplatesAsync,
        tenantId,
        businessContextId,
        templateId
      );

      template = decodeTemplateContent(template);
      template = setCampaignOccurrenceType(template);
    }

    let eventMetaData;
    if (template.eventTypeCategory) {
      let schemaIdentifier = template.productCategory
        ? `${template.productCategory}.${template.productName}.${template.eventTypeCategory}`
        : '';
      yieldAlls.eventMetaData = yield call(waitForGetEventMetaData, {
        campaignOccurrenceType: template.campaignOccurrenceType,
        productCategory: template.productCategory,
        eventTypeCategory: template.eventTypeCategory,
        productName: template.productName,
        schemaIdentifier,
        workflow: 'template',
        languageCode: template?.languageCode || null
      });
    }

    ({ eventMetaData: { eventMetaData } = {} } = yield all(yieldAlls));

    yield put(
      actions.gettingPrintTemplateSucceeded({
        template,
        eventMetaData
      })
    );
  } catch (ex) {
    yield put(actions.gettingPrintTemplateFailed(ex));
  }
}

export function* createTemplateFromCurrent() {
  try {
    yield put(actions.creatingPrintTemplate());
    let tenantId = yield select(currentTenantId);
    let businessContextId = yield select(currentBusinessContextId);
    let template = yield select(currentTemplate);
    let eventMetaData = template.eventMetaData;
    if (!template.id) {
      template = encodeTemplateContent({ ...template });
      template.tenantId = tenantId;
      template.businessContextId = businessContextId;
      template = yield call(
        createTemplateAsync,
        tenantId,
        businessContextId,
        template
      );
    }
    postMessage(template);
    template = decodeTemplateContent(template);
    yield put(
      actions.creatingPrintTemplateSucceeded({
        ...template,
        eventMetaData: eventMetaData
      })
    );
  } catch (ex) {
    yield put(actions.creatingPrintTemplateFailed(ex));
  }
}

export function* updateTemplateFromCurrent() {
  try {
    yield put(actions.updatingPrintTemplate());
    let tenantId = yield select(currentTenantId);
    let businessContextId = yield select(currentBusinessContextId);
    let template = yield select(currentTemplate);
    let eventMetaData = template.eventMetaData;

    if (template.id) {
      template = encodeTemplateContent({ ...template });
      template = yield call(
        updateTemplateAsync,
        tenantId,
        businessContextId,
        template
      );
    }
    template = decodeTemplateContent(template);
    yield put(
      actions.updatingPrintTemplateSucceeded({
        ...template,
        eventMetaData: eventMetaData
      })
    );
  } catch (ex) {
    yield put(actions.updatingPrintTemplateFailed(ex));
  }
}

export function* warnAndArchive(action) {
  yield put(
    campaignActions.getAssociatedCampaignsFor({
      id: action.payload.id,
      typeOf: types.PRINT_TEMPLATE
    })
  );
  yield take(actions.GETTING_ASSOCIATED_CAMPAIGNS_FOR_PRINT_TEMPLATE_SUCCEEDED);
  let templates = yield select(paginatedTemplateList);
  let templateToWarnAbout = templates.find(
    template => template.id === action.payload.id
  );
  action.payload.message.props = {
    ...action.payload.message.props,
    template: templateToWarnAbout,
    type: 'template'
  };
  yield put(userMessageActions.setMessage(action.payload.message));
}

export function* deleteTemplatesByIdList(action) {
  const { payload } = action;
  try {
    let tenantId = yield select(currentTenantId);
    let businessContextId = yield select(currentBusinessContextId);
    let templateIds = payload.templateIds;
    let templates = yield select(paginatedTemplateList);

    let deletedTemplateStatuses = [];
    if (templateIds.length > 0) {
      yield put(actions.deletingPrintTemplates());
      // all() prallelizes delete operations and waits until they all finish.
      // if the delete template API method can be made to return the deleted id this could be set to a variable and report partial success which would make for better failure handling.
      if (payload.hardDelete) {
        deletedTemplateStatuses = yield all(
          templateIds
            .map(templateId => [
              deleteTemplateAsync,
              tenantId,
              businessContextId,
              templateId
            ])
            .map(reflect)
        );
      } else {
        deletedTemplateStatuses = yield all(
          templateIds
            .map(templateId => {
              const template = templates.find(t => t.id === templateId);
              const encodedTemplate = encodeTemplateContent({ ...template });
              return [
                updateTemplateAsync,
                tenantId,
                businessContextId,
                {
                  ...encodedTemplate,
                  active: !payload.activeStatus
                }
              ];
            })
            .map(reflect)
        );
      }
    }
    if (
      deletedTemplateStatuses.filter(status => status.status === 'resolved')
        .length === templateIds.length
    ) {
      isArchiveInProgress = true;
      yield all([
        put(
          actions.deletingPrintTemplatesSucceeded(
            templateIds,
            payload.hardDelete
          )
        ),
        put(actions.getPaginatedPrintTemplates({ force: true }))
      ]);
    } else {
      yield put(
        actions.deletingPrintTemplatesFailed({
          message: 'Not all requested templates could be deleted.',
          deletedTemplateStatuses
        })
      );
    }
  } catch (ex) {
    isArchiveInProgress = false;
    yield put(
      actions.deletingPrintTemplatesFailed({
        ...ex,
        hardDelete: payload.hardDelete
      })
    );
  }
}

let timer;
export function* autoSaveTimerStart() {
  if (timer) {
    clearTimeout(timer);
  }
  yield call(
    () =>
      new Promise(resolve => {
        timer = setTimeout(resolve, AUTO_SAVE_TIMER_MS);
      })
  );

  yield put(actions.printAutoSaveTimerEnd());
}

export function* getPrintFile(action) {
  const { payload = {} } = action;
  try {
    let tenantId = yield select(currentTenantId);
    let businessContextId = yield select(currentBusinessContextId);
    let response = yield call(
      getSampleAsync,
      tenantId,
      businessContextId,
      payload
    );
    yield put(actions.gettingPrintFileSucceeded(response));
  } catch (ex) {
    yield put(actions.gettingPrintFileFailed(ex));
  }
}